import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import ReactPlayer from "react-player";
import { useTranslation } from "react-i18next";

// Components
import DashboardGuidedTour from "../dashboard/DashboardGuidedTour";
import UserAccountPopover from "../components/layout/UserAccountPopover";
import NavigationSidebar from "../components/layout/NavigationSidebar";
import NotificationToast from "../components/scheduler/NotificationToast";
import StartSite from "../components/welcome/StartSite";

// Icons
import AccountCircle from "@mui/icons-material/AccountCircle";
import QuestionMarkCircle from "@mui/icons-material/Help";
import VideoCameraIcon from "@mui/icons-material/Videocam";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";

// Assets
import DynamicDeskLogo from "../assets/DD_2_Weiss.png";

// Utils and services
import { useAuthStore } from "../stores/zustand";
import { useTourStore } from "../stores/zustand";
import { useHomeDataQuery } from "../stores/tanstack/queries/useHomeDataQuery";
import { authApi, settingsApi } from "../services";
import { useFirebaseNotifications } from "../hooks/useFirebaseNotifications";
import { useLocalNotifications } from "../hooks/useLocalNotifications";

import { useQueryClient } from "@tanstack/react-query";

/**
 * MainContainer Component
 *
 * Main application shell and layout wrapper. Responsible for:
 * - App header with navigation controls (tour, help, account menu)
 * - Sidebar navigation based on user product type
 * - Routing outlet for nested page components (calendar, library, settings, feedback)
 * - Firebase toast notifications display
 * - Guided tour and video tutorial dialogs
 * - StartSite overlay for course users (first-time onboarding)
 * - Language switching and authentication lifecycle management
 *
 * Uses React Router for client-side navigation with proper URL state reflection.
 * Manages physio therapist access restrictions and feature-based conditional rendering.
 *
 * @returns {JSX.Element} Main application container with header, sidebar, and outlet
 */
const MainContainer = () => {
  // Translation and routing hooks
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Auth and product context
  const {
    user,
    currentProduct,
    clearAuth,
    preferredLanguage,
    setPreferredLanguage,
  } = useAuthStore();
  const { data: homeData } = useHomeDataQuery();
  const isPhysioTherapist = currentProduct === "physio_therapist";
  const { nextStep } = useTourStore();
  const queryClient = useQueryClient();

  // Firebase notifications hook for push notification setup
  const {
    showToast,
    currentNotification,
    dismissToast,
    getNotificationPermission,
    handleEnablePushNotifications,
    isElectron,
  } = useFirebaseNotifications();

  useLocalNotifications({
    enabled: true,
    autoStartSync: true,
    homeData: homeData,
  });

  // Memoize feature access flags to prevent unnecessary recalculations
  const { hasCourseAccess, hasCorporateAccess } = useMemo(
    () => ({
      hasCourseAccess: currentProduct === "preventive_course",
      hasCorporateAccess: currentProduct === "corporate",
    }),
    [currentProduct],
  );

  // UI state management for dialogs and tours
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
  const [showStartSite, setShowStartSite] = useState(hasCourseAccess);
  const [anchorEl, setAnchorEl] = useState(null);

  // Extract authentication flow data from route location state
  // These flags indicate how user arrived at this page
  const isFirstLogin = location.state?.isFirstLogin;
  const wasAutoLogin = location.state?.autoLogin;
  const wasStartup = location.state?.wasStartup;

  // Derive current page from URL pathname for sidebar highlighting
  // and to track which content area should be displayed
  const currentContent = useMemo(() => {
    if (showStartSite) return "startSite";
    if (location.pathname.includes("/calendar")) return "schedule";
    if (location.pathname.includes("/schedule")) return "schedule";
    if (location.pathname.includes("/library")) return "library";
    if (location.pathname.includes("/settings")) return "settings";
    if (location.pathname.includes("/feedback")) return "feedback";
    if (location.pathname.includes("/test")) return "test";
    return "schedule"; // default
  }, [location.pathname, showStartSite]);

  // Restrict physio therapist users to physio-specific routes
  useEffect(() => {
    const allowedPhysioPrefixes = ["/physio", "/physio-video-selection"];
    const isAllowedForPhysio = allowedPhysioPrefixes.some((pref) =>
      location.pathname.startsWith(pref),
    );
    const allowedExceptions = ["/login", "/logout", "/"];

    if (
      isPhysioTherapist &&
      !isAllowedForPhysio &&
      !allowedExceptions.includes(location.pathname)
    ) {
      navigate("/physio-video-selection", {
        replace: true,
        state: { from: location.pathname },
      });
    }
  }, [location.pathname, navigate, isPhysioTherapist]);

  // Listen for language changes from Electron menu (desktop app integration)
  // When user changes language in Electron app, sync it with React i18n
  useEffect(() => {
    if (window.electronAPI?.language) {
      const cleanup = window.electronAPI.language.onChange((newLanguage) => {
        i18n.changeLanguage(newLanguage);
        settingsApi.updateLanguage(newLanguage).catch((error) => {
          console.error("Failed to update language in backend:", error);
        });
      });

      return cleanup;
    }
  }, [i18n]);

  // Toggle between English and German language
  // Syncs with AuthStore, i18n, backend, and Electron if available
  const handleLanguageToggle = useCallback(async () => {
    const newLanguage = preferredLanguage === "en" ? "de" : "en";

    // Update Zustand (single source of truth)
    setPreferredLanguage(newLanguage);

    // Sync i18n
    i18n.changeLanguage(newLanguage);

    // Update backend
    try {
      await settingsApi.updateLanguage(newLanguage);
    } catch (error) {
      console.error(
        "[MainContainer] Failed to update language in backend:",
        error,
      );
    }

    // Notify Electron if present
    if (window.electronAPI?.language) {
      window.electronAPI.language.notifyChange(newLanguage);
    }
  }, [preferredLanguage, setPreferredLanguage, i18n]);

  // Update user password with validation of current password
  const handlePasswordUpdate = useCallback(
    async (currentPassword, newPassword) => {
      try {
        await settingsApi.updatePassword(currentPassword, newPassword);
        return { success: true, message: t("passwordUpdated") };
      } catch (error) {
        console.error("Failed to update password:", error);

        if (error.response?.status === 403) {
          return { success: false, message: t("incorrectCurrentPassword") };
        }

        return { success: false, message: t("passwordUpdateFailed") };
      }
    },
    [t],
  );

  // Logout: clear caches, auth state, and navigate to login
  // Properly cleans up TanStack Query cache and Zustand auth store
  const handleLogout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout API call failed:", error);
    }

    // Clear TanStack Query cache
    queryClient.clear();

    // Clear AuthStore
    clearAuth();

    if (window.electronAPI?.navigation) {
      try {
        await window.electronAPI.navigation.navigateToAuthenticatedState(false);
      } catch (navError) {
        console.warn("[MainContainer] Electron navigation failed:", navError);
        navigate("/");
      }
    } else {
      navigate("/");
    }
  }, [queryClient, clearAuth, navigate]);

  // Video dialog handlers for training video playback
  const handleOpenVideoDialog = useCallback(() => {
    setIsVideoDialogOpen(true);
  }, []);

  const handleCloseVideoDialog = useCallback(() => {
    setIsVideoDialogOpen(false);
  }, []);

  // Get correct video URL based on current language
  // English and German versions use different Vimeo videos
  const videoUrl = useMemo(() => {
    return preferredLanguage === "en"
      ? "https://vimeo.com/1031253750"
      : "https://vimeo.com/1033152791";
  }, [preferredLanguage]);

  // Tour handlers: start guided tour (or skip StartSite overlay if course user)
  const startTour = useCallback(() => {
    if (hasCourseAccess && showStartSite) {
      setShowStartSite(false);
      navigate("/home/calendar");
      setTimeout(() => {
        setIsTourOpen(true);
      }, 1000);
    } else {
      setIsTourOpen(true);
    }
  }, [hasCourseAccess, showStartSite, navigate]);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  // Account popover handlers for user menu
  const handleAccountClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleAccountClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Navigate to notification test page
  const handleTestNotifications = useCallback(() => {
    navigate("/home/test");
  }, [navigate]);

  // Handle StartSite navigation: transition from onboarding to main calendar
  const handleStartSiteContentChange = useCallback(
    (newContent) => {
      if (newContent === "schedule") {
        setShowStartSite(false);
        navigate("/home/calendar");
        nextStep();
      }
    },
    [navigate, nextStep],
  );

  // Main content area uses React Router Outlet for nested page rendering
  return (
    <div style={{ display: "flex" }}>
      {/* App Header */}
      <AppBar position="fixed" sx={{ backgroundColor: "#00003e" }}>
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "15px",
          }}
        >
          <Box>
            <img
              src={DynamicDeskLogo}
              style={{ width: "auto", height: "50px" }}
              alt="DynamicDeskLogo"
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Product Type Badge - displays user's product subscription */}
            {/* Only shown for corporate or course users */}
            {hasCorporateAccess && (
              <Box
                sx={{
                  top: 10,
                  right: (theme) => `calc(${theme.spacing(2)} + 160px)`,
                  minWidth: 220,
                  textAlign: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontSize: "0.875rem",
                  fontWeight: "medium",
                }}
              >
                Desk Dynamics Premium
              </Box>
            )}
            {hasCourseAccess && (
              <Box
                sx={{
                  top: 10,
                  right: (theme) => `calc(${theme.spacing(2)} + 160px)`,
                  minWidth: 220,
                  textAlign: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontSize: "0.875rem",
                  fontWeight: "medium",
                }}
              >
                Präventivkurs
              </Box>
            )}
            <IconButton
              className="video-button"
              size="large"
              aria-label="tutorial video"
              color="inherit"
              onClick={handleOpenVideoDialog}
            >
              <VideoCameraIcon style={{ fontSize: "32px" }} />
            </IconButton>
            {/* Info button for course users - opens StartSite overlay */}
            {hasCourseAccess && (
              <IconButton
                size="large"
                aria-label="instructions"
                color="inherit"
                onClick={() => setShowStartSite(true)}
              >
                <InfoIcon style={{ fontSize: "32px" }} />
              </IconButton>
            )}
            {/* Guided tour button - starts interactive tour of calendar features */}
            <IconButton
              size="large"
              aria-label="start guided tour"
              color="inherit"
              onClick={startTour}
            >
              <QuestionMarkCircle style={{ fontSize: "32px" }} />
            </IconButton>
            {/* Account menu button - opens popover for user settings, logout, etc */}
            <IconButton
              size="large"
              aria-label="account menu"
              color="inherit"
              onClick={handleAccountClick}
            >
              <AccountCircle style={{ fontSize: "32px" }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Account Popover - language, password, notifications, logout options */}
      <UserAccountPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleAccountClose}
        onLanguageToggle={handleLanguageToggle}
        onPasswordUpdate={handlePasswordUpdate}
        onLogout={handleLogout}
        currentLanguage={preferredLanguage}
        userEmail={user.email}
        notificationPermission={getNotificationPermission()}
        onEnableNotifications={handleEnablePushNotifications}
        isElectron={isElectron}
        onTestNotifications={handleTestNotifications}
      />

      {/* Navigation Sidebar - shows different menu items based on user product type */}
      <NavigationSidebar currentContent={currentContent} />

      {/* Main Content Area - stretches to fill remaining space after sidebar */}
      {/* Contains StartSite overlay (for course users) and React Router outlet (for other pages) */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "90%",
          height: "90vh",
          paddingTop: 6,
          paddingBottom: 2,
          marginLeft: "2px",
          paddingRight: 4,
          transition: "margin-left 225ms cubic-bezier(0.0, 0, 0.2, 1) 0ms",
        }}
      >
        <Toolbar />
        <Container>
          {/* StartSite Overlay - course user onboarding flow displayed on top */}
          {/* Hides when user progresses to calendar or closes overlay */}
          {showStartSite && (
            <StartSite
              setContent={handleStartSiteContentChange}
              language={i18n.language}
              setIsTourOpen={setIsTourOpen}
              OpenVideoDialog={handleOpenVideoDialog}
            />
          )}

          {/* Main Content - rendered by React Router based on current route */}
          {/* Outlet receives isFirstLogin, wasAutoLogin, wasStartup from location state */}
          {!showStartSite && (
            <Outlet
              context={{
                isFirstLogin,
                wasAutoLogin,
                wasStartup,
                isMobile: false,
              }}
            />
          )}
        </Container>
      </Box>

      {/* Guided Tour Modal - interactive step-by-step tour of calendar features */}
      <DashboardGuidedTour
        isTourOpen={isTourOpen}
        closeTour={closeTour}
        currentPage={currentContent}
      />

      {/* Toast Notification - Firebase push notification feedback display */}
      <NotificationToast
        show={showToast}
        notification={currentNotification}
        onClose={dismissToast}
        autoHideDuration={6000}
      />

      {/* Training Video Dialog - embedded Vimeo video with language-specific versions */}
      <Dialog
        open={isVideoDialogOpen}
        onClose={handleCloseVideoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <IconButton
            onClick={handleCloseVideoDialog}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <ReactPlayer
            url={videoUrl}
            controls={true}
            width="100%"
            height="500px"
            onEnded={handleCloseVideoDialog}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MainContainer;
